import { useQuery } from '@hooks/reactQuery';

import { getOranSchemaConfig, getSchemaConfig } from '@api/schemaConfig';

import { extractQueryParams } from '@utils/common';

import { ISchemaConfigEntity, ISchemaConfigItem } from '@interfaces/cmModule/schemaConfig';
import { TDefaultDTO } from '@interfaces/common/defaultDTO';

import { refetchOptions } from '@constants/common';
import { MAVENIR_SCHEMA_CONFIG } from '@constants/queryKeys';

interface ISchemaConfigsReturnType {
	schemaConfig: {
		name: string;
		subRows: ISchemaConfigEntity[];
	}[];
	countOfRecords: number;
	isChanged: boolean;
	isSet: boolean;
	updatedAt: number | null;
	isLoadingSchemaConfig: boolean;
}

const useQuerySchemaConfig = (params: TDefaultDTO & {
	isOranVendor: boolean;
	vendor?: string;
}): ISchemaConfigsReturnType => {
	const {
		page,
		pageSize,
		sortFieldName,
		sortDirection,
		isOranVendor,
		vendor,
	} = params;

	const queryFn = isOranVendor ? getOranSchemaConfig : getSchemaConfig;

	const {
		data: schemaConfig,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [
			MAVENIR_SCHEMA_CONFIG,
			{
				page,
				pageSize,
				sortFieldName,
				sortDirection,
			},
			vendor,
		],
		enabled: [!!vendor],
		queryFn: extractQueryParams(queryFn),
		select: (data) => {
			return {
				items: data?.items?.items?.map((item: ISchemaConfigItem) => ({
					name: item.name || item.presetName,
					subRows: item.entities.length ? item.entities : undefined,
					folderId: item?.folderId,
					isRebuildAvailable: item?.isRebuildAvailable,
				})) || [],
				count: data?.items?.count || 0,
				isChanged: data?.isChanged,
				isSet: data?.isSet,
				updatedAt: data?.updatedAt,
			};
		},
		...refetchOptions,
	});

	return {
		schemaConfig: schemaConfig?.items || [],
		countOfRecords: schemaConfig?.count || 0,
		isChanged: schemaConfig?.isChanged || false,
		isSet: schemaConfig?.isSet || false,
		updatedAt: schemaConfig?.updatedAt || null,
		isLoadingSchemaConfig: isLoading || isFetching,
	};
};

export default useQuerySchemaConfig;
